import { useEffect, useState } from "react";
import { Badge, DatePicker, Select, Spin, Button, Modal } from "antd";
import { FilterOutlined, FileExcelOutlined, FilePdfOutlined, FileTextOutlined, DownloadOutlined } from "@ant-design/icons";
import FilterPanel from "../hiring/FilterPanel";
import "../hiring/FilterPanel.css";
import dayjs from "dayjs";
import { loadAllAttendancePaginated } from "../../redux/rtk/features/attendance/attendanceSlice";
import { useDispatch, useSelector } from "react-redux";
import { CSVLink } from "react-csv";
import { CsvLinkBtn } from "../UI/CsvLinkBtn";
import { getAllMaster } from "../../redux/rtk/features/masters/masterSlice";
import Attendance from "./AddAttendance";
import getRoleIdFromToken from "../../utils/getRoleIdByToken";
import ExcelJS from "exceljs";
import ColVisibilityDropdown from "../Shared/ColVisibilityDropdown";

const AllAttendanceCalendar = () => {
  const dispatch = useDispatch();
  const today = dayjs();
  const [selectedYear, setSelectedYear] = useState(today.year());
  const [selectedMonth, setSelectedMonth] = useState(today.month() + 1);
  const [selectedCompanyOfficeIds, setSelectedCompanyOfficeIds] = useState([]);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [dateRange, setDateRange] = useState(null);
  const { list: attendanceData, loading } =
    useSelector((state) => state.attendance) || {};
  const masterData = useSelector((state) => state.masters.masters) || {};
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [canCreateAttendance, setCanCreateAttendance] = useState(false);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [columnsToShow, setColumnsToShow] = useState([]);
  const RoleId = getRoleIdFromToken();
  
  // Fetch menu permissions and check if user can create attendance
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        setPermissionsLoading(true);
        const response = await fetch(
          `https://hrmsapi.softomation.com/roles/MenuPermissionGetByRoleId?RoleId=${RoleId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("access-token")}`,
            },
          }
        );
        
        if (!response.ok) {
          throw new Error("Failed to fetch permissions");
        }
        
        const permissions = await response.json();
        
        // Check if attendance menu has DataCreate permission
        const attendancePermission = permissions.find(
          (menu) => menu.MenuKey === "attendance" && menu.DataCreate === true
        );
        
        setCanCreateAttendance(!!attendancePermission);
      } catch (error) {
        // console.error("Error fetching permissions:", error);
        setCanCreateAttendance(false);
      } finally {
        setPermissionsLoading(false);
      }
    };
    
    if (RoleId) {
      fetchPermissions();
    }
  }, [RoleId]);

  // Get available employees based on selected companies
  useEffect(() => {
    if (Array.isArray(attendanceData)) {
      let employees = attendanceData;

      if (selectedCompanyOfficeIds.length > 0) {
        employees = employees.filter((employee) =>
          selectedCompanyOfficeIds.includes(employee.CompanyOfficeId)
        );
      }

      setAvailableEmployees(employees);
    } else {
      setAvailableEmployees([]);
    }
  }, [attendanceData, selectedCompanyOfficeIds]);

  // Apply all filters  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    if (Array.isArray(attendanceData)) {
      let filteredData = attendanceData;

      if (selectedCompanyOfficeIds.length > 0) {
        filteredData = filteredData.filter((employee) =>
          selectedCompanyOfficeIds.includes(employee.CompanyOfficeId)
        );
      }

      if (selectedEmployeeIds.length > 0) {
        filteredData = filteredData.filter((employee) =>
          selectedEmployeeIds.includes(employee.EmployeeId)
        );
      }

      if (dateRange && dateRange[0] && dateRange[1]) {
        const startDate = dateRange[0].format("YYYY-MM-DD") + "T00:00:00.000Z";
        const endDate = dateRange[1].format("YYYY-MM-DD") + "T23:59:59.999Z";

        filteredData = filteredData
          .map((employee) => ({
            ...employee,
            attendance: employee.attendance.filter((record) => {
              const recordDate = new Date(record.AttendanceDate);
              const start = new Date(startDate);
              const end = new Date(endDate);
              return recordDate >= start && recordDate <= end;
            }),
          }))
          .filter((employee) => employee.attendance.length > 0);
      }

      setFilteredEmployees(filteredData);
    } else {
      setFilteredEmployees([]);
    }
  }, [
    attendanceData,
    selectedCompanyOfficeIds,
    selectedEmployeeIds,
    dateRange,
  ]);

  useEffect(() => {
    dispatch(
      loadAllAttendancePaginated({
        Month: selectedMonth,
        Year: selectedYear,
      })
    );
    dispatch(getAllMaster());
  }, [dispatch, selectedMonth, selectedYear]);

  const handleMonthChange = (date) => {
    if (date) {
      setSelectedYear(date.year());
      setSelectedMonth(date.month() + 1);
    }
  };

  const handleCompanyChange = (values) => {
    setSelectedCompanyOfficeIds(values || []);
    setSelectedEmployeeIds([]);
  };

  const handleEmployeeChange = (values) => {
    setSelectedEmployeeIds(values || []);
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
  };

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const response = await fetch(
        "https://hrmsapi.softomation.com/attendance/DownloadEmployeeMonthelyAttandanceByCompanyId",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access-token")}`,
          },
          body: JSON.stringify({
            Month: selectedMonth,
            Year: selectedYear,
          }),
        }
      );
      if (!response.ok) {
        throw new Error("Failed to download PDF");
      }
      const data = await response.json();
      if (!data.ReportName) {
        throw new Error("Invalid response from server");
      }
      const pdfUrl = `https://hrmsapi.softomation.com${data.ReportName}`;
      window.open(pdfUrl, "_blank");
    } catch (error) {
      // console.error("Error downloading PDF:", error);
    } finally {
      setPdfLoading(false);
    }
  };

  const handleSearch = () => {
    const searchData = {
      companies: selectedCompanyOfficeIds,
      employees: selectedEmployeeIds,
      dateRange: dateRange
        ? [
            dateRange[0]?.format("YYYY-MM-DD"),
            dateRange[1]?.format("YYYY-MM-DD"),
          ]
        : null,
    };

    // console.log(
    //   "Search filters applied (JSON):",
    //   JSON.stringify(searchData, null, 2)
    // );

    return searchData;
  };

  const clearFilters = () => {
    setSelectedCompanyOfficeIds([]);
    setSelectedEmployeeIds([]);
    setDateRange(null);
  };

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleModalOk = () => {
    setIsModalVisible(false);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
  };

  const getDaysArray = () => {
    if (dateRange && dateRange[0] && dateRange[1]) {
      const startDate = dateRange[0];
      const endDate = dateRange[1];
      const days = [];

      let currentDate = startDate.clone();
      while (currentDate.diff(endDate, "day") <= 0) {
        days.push({
          day: currentDate.date(),
          dateStr: currentDate.format("YYYY-MM-DD") + "T00:00:00.000Z",
          weekday: currentDate.format("ddd"),
          fullDate: currentDate.format("MMM DD"),
        });
        currentDate = currentDate.add(1, "day");
      }

      return days;
    } else {
      const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
      return Array.from({ length: daysInMonth }, (_, i) => {
        const dayNum = i + 1;
        const dateStr = `${selectedYear}-${selectedMonth
          .toString()
          .padStart(2, "0")}-${dayNum
          .toString()
          .padStart(2, "0")}T00:00:00.000Z`;
        const dateObj = new Date(dateStr);
        const weekday = dateObj.toLocaleDateString("en-US", {
          weekday: "short",
        });
        return { day: dayNum, dateStr, weekday, fullDate: `${dayNum}` };
      });
    }
  };

  const daysArray = getDaysArray();

  // Define columns for column visibility
  const totalCols = ["P", "A", "L", "H", "SD", "WO", "PH", "RH", "NFD", "ECO", "GPD", "APD", "AAO", "MPD", "TD", "WD"];
  const totalColTitles = {
    "P": "Present",
    "A": "Absent",
    "L": "Leave",
    "H": "Half Day",
    "SD": "Short Days",
    "WO": "Weekoff",
    "PH": "Public Holiday",
    "RH": "Restricted Holiday",
    "NFD": "Not Full Day",
    "ECO": "Earn Combo Off Days",
    "GPD": "Grace Presence Days",
    "APD": "Adjusted Partial Day",
    "AAO": "Adjusted All Off",
    "MPD": "Monthly Paid Days",
    "TD": "Total Days",
    "WD": "Working Days"
  };

  const columns = [
    { id: 1, title: "S.No." },
    { id: 2, title: "Office Location" },
    { id: 3, title: "Employee Code" },
    { id: 4, title: "Employee Name" },
    { id: 5, title: "Join Date" },
    { id: 6, title: "Designation" },
    { id: 7, title: "Reporting To" },
    { id: 8, title: "Daily Attendance" },
    ...totalCols.map((col, idx) => ({ id: 9 + idx, title: totalColTitles[col] || col }))
  ];

  // Initialize columns to show
  useEffect(() => {
    setColumnsToShow(columns);
  }, []);

  const columnsToShowHandler = (val) => {
    setColumnsToShow(val);
  };

  // Helper function to check if column should be visible
  const isColumnVisible = (columnTitle) => {
    return columnsToShow.some(col => col.title === columnTitle);
  };

  // Helper function to calculate sticky left position for columns
  const getStickyLeft = (columnTitle) => {
    let left = 0;
    if (columnTitle === "S.No.") return left;
    if (isColumnVisible("S.No.")) left += 50;
    if (columnTitle === "Office Location") return left;
    if (isColumnVisible("Office Location")) left += 120;
    if (columnTitle === "Employee Code") return left;
    if (isColumnVisible("Employee Code")) left += 100;
    if (columnTitle === "Employee Name") return left;
    if (isColumnVisible("Employee Name")) left += 140;
    if (columnTitle === "Join Date") return left;
    if (isColumnVisible("Join Date")) left += 110;
    if (columnTitle === "Designation") return left;
    if (isColumnVisible("Designation")) left += 120;
    if (columnTitle === "Reporting To") return left;
    return left;
  };

  const formatJoinDate = (dateStr) => {
    if (!dateStr) return "";
    const d = dayjs(dateStr);
    return d.isValid() ? d.format("DD-MMM-YYYY") : "";
  };

  // Helper function to format employee name from FirstName + MiddleName + LastName
  const formatEmployeeName = (employee) => {
    if (!employee) return "";
    const firstName = employee.FirstName || "";
    const middleName = employee.MiddleName || "";
    const lastName = employee.LastName || "";
    
    const nameParts = [firstName, middleName, lastName].filter(part => part && part.trim());
    return nameParts.join(" ") || employee.EmployeeName || "";
  };

  // Helper function to get company/office name
  const getCompanyOfficeName = (employee) => {
    if (!employee || !employee.CompanyOfficeId) return "";
    const companyOffice = masterData.CompanyOfficeMaster?.find(
      (office) => office.CompanyOfficeId === employee.CompanyOfficeId
    );
    return companyOffice?.CompanyOfficeName || "";
  };

  // Function to determine attendance status based on logic
  const getAttendanceStatus = (record) => {
    if (!record || Object.keys(record).length === 0) {
      return null; // No record
    }

    // Priority order: Leave > Public Holiday > Restricted > Weekoff > Present checks (SL > H > P) > Absent
    if (record.IsOnLeave) {
      return "L";
    }
    if (record.IsPublicHoliday) {
      return "PH";
    }
    if (record.IsRestricted) {
      return "RH";
    }
    if (record.WorkingDayName === "Weekoff") {
      return "WO";
    }
    
    // If present, check in order: Short Leave > Half Day > Present
    if (record.IsPresent) {
      if (
        record.IsShortLeaveOnStart ||
        record.IsShortLeaveOnMid ||
        record.IsShortLeaveOnEnd
      ) {
        return "SL";
      }
      if (
        record.IsHalfDayOnStart ||
        record.IsHalfDayOnMid ||
        record.IsHalfDayOnEnd
      ) {
        return "H";
      }
      return "P";
    }

    // Default to Absent
    return "A";
  };

  // Sort employees alphabetically by name
  const sortedEmployees = [...(filteredEmployees || [])].sort((a, b) => {
    const nameA = formatEmployeeName(a).toLowerCase();
    const nameB = formatEmployeeName(b).toLowerCase();
    return nameA.localeCompare(nameB);
  });

  const groupedByCompany =
    sortedEmployees?.reduce((acc, employee) => {
      const { EmployeeCode = `Unknown-${employee.employeeId}` } = employee;
      if (!acc[EmployeeCode]) {
        acc[EmployeeCode] = [];
      }
      acc[EmployeeCode].push(employee);
      return acc;
    }, {}) || {};

  const csvHeaders = [
    { label: "Employee Name", key: "EmployeeName" },
    { label: "Attendance Date", key: "AttendanceDate" },
    { label: "In Time", key: "PunchInTime" },
    { label: "Out Time", key: "PunchOutTime" },
    { label: "Status", key: "Status" },
  ];

  const csvData = filteredEmployees.flatMap((emp) =>
    emp.attendance.map((att) => {
      const statusCode = getAttendanceStatus(att);
      const statusMap = {
        "P": "Present",
        "A": "Absent",
        "L": "Leave",
        "H": "HalfDay",
        "SL": "ShortLeave",
        "WO": "Weekoff",
        "PH": "Holiday",
        "RH": "Restricted"
      };
      return {
        EmployeeName: formatEmployeeName(emp),
        AttendanceDate: att.AttendanceDate,
        PunchInTime: att.PunchInTime || "",
        PunchOutTime: att.PunchOutTime || "",
        Status: statusCode ? (statusMap[statusCode] || "Absent") : "Absent",
      };
    })
  );

  // Helper function to get visible total columns for export
  const getVisibleTotalColumns = () => {
    return totalCols.filter(col => {
      const colTitle = totalColTitles[col] || col;
      return isColumnVisible(colTitle);
    });
  };

  // Excel export function
  // Excel export function for Time Sheet (In Time, Out Time, Total Hours)
  const handleDownloadTimeSheetExcel = async () => {
    const monthName = dayjs(`${selectedYear}-${selectedMonth.toString().padStart(2, "0")}-01`).format("MMM-YY");
    
    // Create workbook
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Time Sheet");

    // Helper function to get cell
    const getCell = (row, col) => worksheet.getCell(row, col);

    // Helper function to style cell
    const styleCell = (cell, fillColor, fontColor, bold = false, alignment = "center") => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: fillColor ? `FF${fillColor}` : "FFFFFFFF" },
      };
      cell.font = {
        color: { argb: fontColor ? `FF${fontColor}` : "FF000000" },
        bold: bold,
      };
      cell.alignment = { horizontal: alignment, vertical: "middle" };
      cell.border = {
        top: { style: "thin", color: { argb: "FF000000" } },
        bottom: { style: "thin", color: { argb: "FF000000" } },
        left: { style: "thin", color: { argb: "FF000000" } },
        right: { style: "thin", color: { argb: "FF000000" } },
      };
    };

    let rowNum = 1;

    // Header row
    const headerStyle = { fillColor: "808080", fontColor: "FFFFFF", bold: true };
    
    // Build visible columns dynamically
    let colIndex = 1;
    const columnOrder = [];
    
    if (isColumnVisible("S.No.")) {
      getCell(rowNum, colIndex).value = "S.No.";
      styleCell(getCell(rowNum, colIndex), headerStyle.fillColor, headerStyle.fontColor, headerStyle.bold);
      worksheet.mergeCells(rowNum, colIndex, rowNum + 1, colIndex);
      columnOrder.push({ type: "S.No.", index: colIndex });
      colIndex++;
    }
    
    if (isColumnVisible("Office Location")) {
      getCell(rowNum, colIndex).value = "Office Location";
      styleCell(getCell(rowNum, colIndex), headerStyle.fillColor, headerStyle.fontColor, headerStyle.bold, "left");
      worksheet.mergeCells(rowNum, colIndex, rowNum + 1, colIndex);
      columnOrder.push({ type: "Office Location", index: colIndex });
      colIndex++;
    }
    
    if (isColumnVisible("Employee Code")) {
      getCell(rowNum, colIndex).value = "Employee Code";
      styleCell(getCell(rowNum, colIndex), headerStyle.fillColor, headerStyle.fontColor, headerStyle.bold);
      worksheet.mergeCells(rowNum, colIndex, rowNum + 1, colIndex);
      columnOrder.push({ type: "Employee Code", index: colIndex });
      colIndex++;
    }
    
    if (isColumnVisible("Employee Name")) {
      getCell(rowNum, colIndex).value = "Employee Name";
      styleCell(getCell(rowNum, colIndex), headerStyle.fillColor, headerStyle.fontColor, headerStyle.bold, "left");
      worksheet.mergeCells(rowNum, colIndex, rowNum + 1, colIndex);
      columnOrder.push({ type: "Employee Name", index: colIndex });
      colIndex++;
    }

    if (isColumnVisible("Join Date")) {
      getCell(rowNum, colIndex).value = "Join Date";
      styleCell(getCell(rowNum, colIndex), headerStyle.fillColor, headerStyle.fontColor, headerStyle.bold);
      worksheet.mergeCells(rowNum, colIndex, rowNum + 1, colIndex);
      columnOrder.push({ type: "Join Date", index: colIndex });
      colIndex++;
    }
    
    if (isColumnVisible("Designation")) {
      getCell(rowNum, colIndex).value = "Designation";
      styleCell(getCell(rowNum, colIndex), headerStyle.fillColor, headerStyle.fontColor, headerStyle.bold, "left");
      worksheet.mergeCells(rowNum, colIndex, rowNum + 1, colIndex);
      columnOrder.push({ type: "Designation", index: colIndex });
      colIndex++;
    }
    
    if (isColumnVisible("Reporting To")) {
      getCell(rowNum, colIndex).value = "Reporting To";
      styleCell(getCell(rowNum, colIndex), headerStyle.fillColor, headerStyle.fontColor, headerStyle.bold, "left");
      worksheet.mergeCells(rowNum, colIndex, rowNum + 1, colIndex);
      columnOrder.push({ type: "Reporting To", index: colIndex });
      colIndex++;
    }
    
    const dateStartCol = colIndex;
    // Date columns - each day gets 4 columns: Punch In Location, In Time, Time Out, Total Hours
    if (isColumnVisible("Daily Attendance")) {
      daysArray.forEach((day) => {
        // Main date header spanning 4 columns
        const dateHeaderCell = getCell(rowNum, colIndex);
        dateHeaderCell.value = dateRange ? day.fullDate : `${day.day} (${day.weekday})`;
        styleCell(dateHeaderCell, headerStyle.fillColor, headerStyle.fontColor, headerStyle.bold);
        worksheet.mergeCells(rowNum, colIndex, rowNum, colIndex + 3);
        colIndex += 4;
      });
    }
    
    // Second header row for sub-columns
    rowNum = 2;
    colIndex = dateStartCol;
    if (isColumnVisible("Daily Attendance")) {
      daysArray.forEach((day) => {
      // Punch In Location sub-header
      const locationHeaderCell = getCell(rowNum, colIndex);
      locationHeaderCell.value = "Location";
      styleCell(locationHeaderCell, headerStyle.fillColor, headerStyle.fontColor, headerStyle.bold, "left");
      colIndex++;
      
      // In Time sub-header
      const intimeHeaderCell = getCell(rowNum, colIndex);
      intimeHeaderCell.value = "In Time";
      styleCell(intimeHeaderCell, headerStyle.fillColor, headerStyle.fontColor, headerStyle.bold);
      colIndex++;
      
      // Out Time sub-header
      const timeoutHeaderCell = getCell(rowNum, colIndex);
      timeoutHeaderCell.value = "Out Time";
      styleCell(timeoutHeaderCell, headerStyle.fillColor, headerStyle.fontColor, headerStyle.bold);
      colIndex++;
      
        // Total Hours sub-header
        const hoursHeaderCell = getCell(rowNum, colIndex);
        hoursHeaderCell.value = "Total Hours";
        styleCell(hoursHeaderCell, headerStyle.fillColor, headerStyle.fontColor, headerStyle.bold);
        colIndex++;
      });
    }

    // Employee data rows
    rowNum = 3;
    let serialNumber = 1;
    sortedEmployees.forEach((employee) => {
      let currentColIndex = 1;

      // Serial Number
      if (isColumnVisible("S.No.")) {
        const serialCell = getCell(rowNum, currentColIndex);
        serialCell.value = serialNumber++;
        styleCell(serialCell);
        currentColIndex++;
      } else {
        serialNumber++;
      }

      // Office Location
      if (isColumnVisible("Office Location")) {
        const companyCell = getCell(rowNum, currentColIndex);
        companyCell.value = getCompanyOfficeName(employee);
        styleCell(companyCell, null, null, false, "left");
        currentColIndex++;
      }

      // Employee Code
      if (isColumnVisible("Employee Code")) {
        const empCodeCell = getCell(rowNum, currentColIndex);
        empCodeCell.value = employee.EmployeeCode || "";
        styleCell(empCodeCell);
        currentColIndex++;
      }

      // Employee Name (Left Aligned)
      if (isColumnVisible("Employee Name")) {
        const empNameCell = getCell(rowNum, currentColIndex);
        empNameCell.value = formatEmployeeName(employee);
        styleCell(empNameCell, null, null, false, "left");
        currentColIndex++;
      }

      // Join Date
      if (isColumnVisible("Join Date")) {
        const joinDateCell = getCell(rowNum, currentColIndex);
        joinDateCell.value = formatJoinDate(employee.JoinDate);
        styleCell(joinDateCell);
        currentColIndex++;
      }

      // Designation
      if (isColumnVisible("Designation")) {
        const designationCell = getCell(rowNum, currentColIndex);
        designationCell.value = employee.DesignationName || "";
        styleCell(designationCell, null, null, false, "left");
        currentColIndex++;
      }

      // Reporting To
      if (isColumnVisible("Reporting To")) {
        const reportingToCell = getCell(rowNum, currentColIndex);
        reportingToCell.value = employee.ReportingToName || "";
        styleCell(reportingToCell, null, null, false, "left");
        currentColIndex++;
      }

      // Daily attendance data - 4 separate columns per day: Punch In Location, In Time, Time Out, Total Hours
      if (isColumnVisible("Daily Attendance")) {
        colIndex = dateStartCol; // Reset colIndex to start of date columns for each employee
        daysArray.forEach((day) => {
        const record = (employee.attendance || []).find(
          (r) => {
            if (!r?.AttendanceDate) return false;
            // Compare dates by normalizing both to YYYY-MM-DD format
            const recordDate = dayjs(r.AttendanceDate).format("YYYY-MM-DD");
            const dayDate = dayjs(day.dateStr).format("YYYY-MM-DD");
            return recordDate === dayDate;
          }
        );

        const inTime = record?.PunchInTime || "";
        const outTime = record?.PunchOutTime || "";
        const workingHours = record?.WorkingHours || "";
        const punchInLocation = record?.PunchInLocation || "";
        const punchInValid = record?.PunchInVALID;
        
        // Punch In Location Column
        const locationCell = getCell(rowNum, colIndex);
        locationCell.value = punchInLocation || "";
        styleCell(locationCell, punchInValid === false ? "FFCDD2" : null, null, false, "left");
        locationCell.alignment = { horizontal: "left", vertical: "middle", wrapText: true };
        colIndex++;
        
        // In Time Column
        const intimeCell = getCell(rowNum, colIndex);
        intimeCell.value = inTime || "";
        styleCell(intimeCell);
        colIndex++;
        
        // Time Out Column
        const timeoutCell = getCell(rowNum, colIndex);
        timeoutCell.value = outTime || "";
        styleCell(timeoutCell);
        colIndex++;
        
        // Total Working Hours Column
        const hoursCell = getCell(rowNum, colIndex);
        hoursCell.value = workingHours || "";
        styleCell(hoursCell);
        colIndex++;
        });
      }

      rowNum++;
    });

    // Set column widths dynamically
    let widthColIndex = 1;
    if (isColumnVisible("S.No.")) {
      worksheet.getColumn(widthColIndex++).width = 8;
    }
    if (isColumnVisible("Office Location")) {
      worksheet.getColumn(widthColIndex++).width = 18;
    }
    if (isColumnVisible("Employee Code")) {
      worksheet.getColumn(widthColIndex++).width = 15;
    }
    if (isColumnVisible("Employee Name")) {
      worksheet.getColumn(widthColIndex++).width = 25;
    }
    if (isColumnVisible("Join Date")) {
      worksheet.getColumn(widthColIndex++).width = 14;
    }
    if (isColumnVisible("Designation")) {
      worksheet.getColumn(widthColIndex++).width = 20;
    }
    if (isColumnVisible("Reporting To")) {
      worksheet.getColumn(widthColIndex++).width = 25;
    }
    
    // Date columns (4 columns per day: Location, In Time, Out Time, Total Hours)
    if (isColumnVisible("Daily Attendance")) {
      daysArray.forEach(() => {
        worksheet.getColumn(widthColIndex++).width = 30; // Punch In Location
        worksheet.getColumn(widthColIndex++).width = 12; // In Time
        worksheet.getColumn(widthColIndex++).width = 12; // Time Out
        worksheet.getColumn(widthColIndex++).width = 12; // Total Hours
      });
    }

    const lastCol = widthColIndex - 1;
    const frozenCols = columnOrder.length;

    // Apply auto-filters to header row
    if (lastCol > 0) {
      worksheet.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: 1, column: lastCol },
      };
    }

    // Freeze panes: Freeze visible fixed columns and first 2 rows (headers)
    if (frozenCols > 0) {
      const nextCol = frozenCols + 1;
      worksheet.views = [
        {
          state: "frozen",
          xSplit: frozenCols,
          ySplit: 2,
          topLeftCell: `${String.fromCharCode(64 + nextCol)}3`,
          activeCell: `${String.fromCharCode(64 + nextCol)}3`,
        },
      ];
    }

    // Generate Excel file and download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `TimeSheet_${monthName}.xlsx`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const handleDownloadExcel = async () => {
    const monthName = dayjs(`${selectedYear}-${selectedMonth.toString().padStart(2, "0")}-01`).format("MMM-YY");
    
    // Create workbook
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Attendance");

    // Define pastel color codes
    const colors = {
      P: "C8E6C9", // Pastel Green - Present
      A: "FFCDD2", // Pastel Red - Absent
      L: "FFE0B2", // Pastel Orange - Leave
      SL: "F8BBD0", // Pastel Pink - Short Leave
      PH: "E1BEE7", // Pastel Purple - Public Holiday
      WO: "FFF9C4", // Pastel Yellow - Weekoff
      H: "B2EBF2", // Pastel Cyan - HalfDay
      RH: "E0E0E0", // Light gray - Restricted Holiday
      header: "808080", // Dark gray
      headerText: "FFFFFF", // White
      totalHeader: "E0E0E0", // Light gray
    };

    // Helper function to get cell
    const getCell = (row, col) => worksheet.getCell(row, col);

    // Helper function to style cell
    const styleCell = (cell, fillColor, fontColor, bold = false, alignment = "center") => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: fillColor ? `FF${colors[fillColor] || fillColor}` : "FFFFFFFF" },
      };
      cell.font = {
        color: { argb: fontColor ? `FF${colors[fontColor] || fontColor}` : "FF000000" },
        bold: bold,
      };
      cell.alignment = { horizontal: alignment, vertical: "middle" };
      cell.border = {
        top: { style: "thin", color: { argb: "FF000000" } },
        bottom: { style: "thin", color: { argb: "FF000000" } },
        left: { style: "thin", color: { argb: "FF000000" } },
        right: { style: "thin", color: { argb: "FF000000" } },
      };
    };

    let rowNum = 1;

    // Legend section (A1:B17)
    getCell(rowNum, 1).value = "Code";
    getCell(rowNum, 2).value = "Description";
    styleCell(getCell(rowNum, 1), "header", "headerText");
    styleCell(getCell(rowNum, 2), "header", "headerText");
    
    const legendData = [
      { code: "P", desc: "Present", color: "P" },
      { code: "A", desc: "Absent", color: "A" },
      { code: "WO", desc: "Weekoff", color: "WO" },
      { code: "PH", desc: "Public Holiday", color: "PH" },
      { code: "L", desc: "Leave", color: "L" },
      { code: "H", desc: "HalfDay", color: "H" },
      { code: "SD", desc: "ShortDays", color: "SL" },
      { code: "RH", desc: "Restricted Holiday", color: "RH" },
      { code: "NFD", desc: "Not Full Day", color: "RH" },
      { code: "ECO", desc: "Earn Combo Off Days", color: "WO" },
      { code: "GPD", desc: "Grace Presence Days", color: "WO" },
      { code: "APD", desc: "Adjusted Partial Day", color: "WO" },
      { code: "AAO", desc: "Adjusted All Off", color: "WO" },
      { code: "MPD", desc: "Monthly Paid Days", color: "WO" },
      { code: "TD", desc: "Total Days", color: "WO" },
      { code: "WD", desc: "Working Days", color: "WO" },
    ];

    legendData.forEach((item, idx) => {
      const codeCell = getCell(rowNum + idx + 1, 1);
      codeCell.value = item.code;
      styleCell(codeCell, item.color);
      
      const descCell = getCell(rowNum + idx + 1, 2);
      descCell.value = item.desc;
      styleCell(descCell);
    });

    // Month information (C1:C2)
    const monthHeaderCell = getCell(rowNum, 3);
    monthHeaderCell.value = "Month";
    styleCell(monthHeaderCell, "header", "headerText");
    
    const monthValueCell = getCell(rowNum + 1, 3);
    monthValueCell.value = monthName;
    styleCell(monthValueCell);

    // Main table starts at row 18 (after legend: 1 header + 16 entries + 1 blank = 18)
    rowNum = 18;
    let colIndex = 1;
    const columnOrder2 = [];

    // Serial Number header (merged A10:A11)
    if (isColumnVisible("S.No.")) {
      const serialCell = getCell(rowNum, colIndex);
      serialCell.value = "S.No.";
      styleCell(serialCell, "header", "headerText");
      worksheet.mergeCells(rowNum, colIndex, rowNum + 1, colIndex);
      columnOrder2.push({ type: "S.No.", index: colIndex });
      colIndex++;
    }

    // Office Location header
    if (isColumnVisible("Office Location")) {
      const companyCell = getCell(rowNum, colIndex);
      companyCell.value = "Office Location";
      styleCell(companyCell, "header", "headerText");
      worksheet.mergeCells(rowNum, colIndex, rowNum + 1, colIndex);
      columnOrder2.push({ type: "Office Location", index: colIndex });
      colIndex++;
    }

    // Employee Code header (merged B10:B11)
    if (isColumnVisible("Employee Code")) {
      const empCodeCell = getCell(rowNum, colIndex);
      empCodeCell.value = "Employee Code";
      styleCell(empCodeCell, "header", "headerText");
      worksheet.mergeCells(rowNum, colIndex, rowNum + 1, colIndex);
      columnOrder2.push({ type: "Employee Code", index: colIndex });
      colIndex++;
    }

    // Employee Name header (merged C10:C11)
    if (isColumnVisible("Employee Name")) {
      const empNameCell = getCell(rowNum, colIndex);
      empNameCell.value = "Employee Name";
      styleCell(empNameCell, "header", "headerText");
      worksheet.mergeCells(rowNum, colIndex, rowNum + 1, colIndex);
      columnOrder2.push({ type: "Employee Name", index: colIndex });
      colIndex++;
    }

    if (isColumnVisible("Join Date")) {
      const joinDateHeaderCell = getCell(rowNum, colIndex);
      joinDateHeaderCell.value = "Join Date";
      styleCell(joinDateHeaderCell, "header", "headerText");
      worksheet.mergeCells(rowNum, colIndex, rowNum + 1, colIndex);
      columnOrder2.push({ type: "Join Date", index: colIndex });
      colIndex++;
    }

    // Designation header (merged D10:D11)
    if (isColumnVisible("Designation")) {
      const designationCell = getCell(rowNum, colIndex);
      designationCell.value = "Designation";
      styleCell(designationCell, "header", "headerText");
      worksheet.mergeCells(rowNum, colIndex, rowNum + 1, colIndex);
      columnOrder2.push({ type: "Designation", index: colIndex });
      colIndex++;
    }

    // Reporting To header (merged E10:E11)
    if (isColumnVisible("Reporting To")) {
      const reportingToCell = getCell(rowNum, colIndex);
      reportingToCell.value = "Reporting To";
      styleCell(reportingToCell, "header", "headerText");
      worksheet.mergeCells(rowNum, colIndex, rowNum + 1, colIndex);
      columnOrder2.push({ type: "Reporting To", index: colIndex });
      colIndex++;
    }

    // Daily columns headers
    if (isColumnVisible("Daily Attendance")) {
      daysArray.forEach((day) => {
        const dayCell = getCell(rowNum, colIndex);
        dayCell.value = String(day.day).padStart(2, "0");
        styleCell(dayCell, "header", "headerText");
        
        const weekdayCell = getCell(rowNum + 1, colIndex);
        weekdayCell.value = day.weekday;
        styleCell(weekdayCell, "header", "headerText");
        colIndex++;
      });
    }

    // Total headers
    const visibleTotalCols = getVisibleTotalColumns();
    const totalStartColIndex = colIndex;
    
    // Merge Total header across all visible total columns
    if (visibleTotalCols.length > 0) {
      const totalHeaderCell = getCell(rowNum, colIndex);
      totalHeaderCell.value = "Total";
      styleCell(totalHeaderCell, "totalHeader");
      worksheet.mergeCells(rowNum, colIndex, rowNum, colIndex + visibleTotalCols.length - 1);
      
      visibleTotalCols.forEach((totalCol, idx) => {
        const totalCell = getCell(rowNum + 1, colIndex + idx);
        totalCell.value = totalCol;
        styleCell(totalCell, "header", "headerText");
      });
    }

    // Employee data rows
    rowNum = 20;
    let serialNumber = 1;
    sortedEmployees.forEach((employee) => {
      let currentColIndex = 1;

      // Serial Number
      if (isColumnVisible("S.No.")) {
        const serialDataCell = getCell(rowNum, currentColIndex);
        serialDataCell.value = serialNumber++;
        styleCell(serialDataCell);
        currentColIndex++;
      } else {
        serialNumber++;
      }

      // Office Location
      if (isColumnVisible("Office Location")) {
        const companyDataCell = getCell(rowNum, currentColIndex);
        companyDataCell.value = getCompanyOfficeName(employee);
        styleCell(companyDataCell, null, null, false, "left");
        currentColIndex++;
      }

      // Employee Code
      if (isColumnVisible("Employee Code")) {
        const empCodeDataCell = getCell(rowNum, currentColIndex);
        empCodeDataCell.value = employee.EmployeeCode || "";
        styleCell(empCodeDataCell);
        currentColIndex++;
      }

      // Employee Name (Left Aligned)
      if (isColumnVisible("Employee Name")) {
        const empNameDataCell = getCell(rowNum, currentColIndex);
        empNameDataCell.value = formatEmployeeName(employee);
        styleCell(empNameDataCell, null, null, false, "left");
        currentColIndex++;
      }

      // Join Date
      if (isColumnVisible("Join Date")) {
        const joinDateDataCell = getCell(rowNum, currentColIndex);
        joinDateDataCell.value = formatJoinDate(employee.JoinDate);
        styleCell(joinDateDataCell);
        currentColIndex++;
      }

      // Designation
      if (isColumnVisible("Designation")) {
        const designationDataCell = getCell(rowNum, currentColIndex);
        designationDataCell.value = employee.DesignationName || "";
        styleCell(designationDataCell, null, null, false, "left");
        currentColIndex++;
      }

      // Reporting To
      if (isColumnVisible("Reporting To")) {
        const reportingToDataCell = getCell(rowNum, currentColIndex);
        reportingToDataCell.value = employee.ReportingToName || "";
        styleCell(reportingToDataCell, null, null, false, "left");
        currentColIndex++;
      }

      // Daily attendance
      if (isColumnVisible("Daily Attendance")) {
        daysArray.forEach((day) => {
          const record = (employee.attendance || []).find(
            (r) => r?.AttendanceDate === day.dateStr
          );
          const status = getAttendanceStatus(record);
          const dayCell = getCell(rowNum, currentColIndex);

          if (status) {
            dayCell.value = status;
            styleCell(dayCell, status);
          } else {
            styleCell(dayCell);
          }

          currentColIndex++;
        });
      }

      // Total columns - Get from API data (only visible ones)
      const totalColMapping = {
        "Present": () => employee.PresentDays || 0,
        "Absent": () => employee.AbsentDays || 0,
        "Leave": () => employee.LeaveDays || 0,
        "Half Day": () => employee.HalfDays || 0,
        "Short Days": () => employee.ShortDays || 0,
        "Weekoff": () => employee.WeekOffs || 0,
        "Public Holiday": () => employee.PublicHolidays || 0,
        "Restricted Holiday": () => employee.RestrictedHolidays || 0,
        "Not Full Day": () => employee.NotFullDay || 0,
        "Earn Combo Off Days": () => employee.EarnComboOffDays || 0,
        "Grace Presence Days": () => employee.GracePresenceDays || 0,
        "Adjusted Partial Day": () => employee.AdjestedPartialDay || 0,
        "Adjusted All Off": () => employee.AdjestedAllOff || 0,
        "Monthly Paid Days": () => employee.MonthelyPaidDays || 0,
        "Total Days": () => employee.TotalDays || 0,
        "Working Days": () => employee.WorkingDays || 0,
      };

      visibleTotalCols.forEach((totalCol) => {
        const colTitle = totalColTitles[totalCol] || totalCol;
        const totalCell = getCell(rowNum, currentColIndex);
        const value = totalColMapping[colTitle] ? totalColMapping[colTitle]() : 0;
        totalCell.value = value;
        styleCell(totalCell);
        currentColIndex++;
      });

      rowNum++;
    });

    // Set column widths dynamically
    let widthColIndex2 = 1;
    if (isColumnVisible("S.No.")) {
      worksheet.getColumn(widthColIndex2++).width = 8;
    }
    if (isColumnVisible("Office Location")) {
      worksheet.getColumn(widthColIndex2++).width = 18;
    }
    if (isColumnVisible("Employee Code")) {
      worksheet.getColumn(widthColIndex2++).width = 15;
    }
    if (isColumnVisible("Employee Name")) {
      worksheet.getColumn(widthColIndex2++).width = 20;
    }
    if (isColumnVisible("Join Date")) {
      worksheet.getColumn(widthColIndex2++).width = 14;
    }
    if (isColumnVisible("Designation")) {
      worksheet.getColumn(widthColIndex2++).width = 20;
    }
    if (isColumnVisible("Reporting To")) {
      worksheet.getColumn(widthColIndex2++).width = 25;
    }
    if (isColumnVisible("Daily Attendance")) {
      for (let i = 0; i < daysArray.length; i++) {
        worksheet.getColumn(widthColIndex2++).width = 5; // Daily columns
      }
    }
    for (let i = 0; i < visibleTotalCols.length; i++) {
      worksheet.getColumn(widthColIndex2++).width = 8; // Total columns
    }

    const lastCol2 = widthColIndex2 - 1;
    const frozenCols2 = columnOrder2.length;

    // Apply auto-filters to header row (row 18)
    if (lastCol2 > 0) {
      worksheet.autoFilter = {
        from: { row: 18, column: 1 },
        to: { row: 18, column: lastCol2 },
      };
    }

    // Freeze panes: Freeze visible fixed columns and first 19 rows (legend, month info, headers)
    if (frozenCols2 > 0) {
      const nextCol2 = frozenCols2 + 1;
      worksheet.views = [
        {
          state: "frozen",
          xSplit: frozenCols2,
          ySplit: 19,
          topLeftCell: `${String.fromCharCode(64 + nextCol2)}20`,
          activeCell: `${String.fromCharCode(64 + nextCol2)}20`,
        },
      ];
    }

    // Generate Excel file and download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Attendance_${monthName}.xlsx`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const statusLegend = [
    { status: "Present", color: "bg-green-500" },
    { status: "Absent", color: "bg-red-500" },
    { status: "Weekoff", color: "bg-yellow-500" },
    { status: "Holiday", color: "bg-purple-500" },
    { status: "Leave", color: "bg-orange-500" },
    { status: "HalfDay", color: "bg-cyan-500" },
    { status: "ShortLeave", color: "bg-pink-500" },
    { status: "Not Recorded", color: "bg-gray-400" },
  ];

  return (
    <div className="p-4 attendance-container">
      <div className="flex justify-between items-center mb-4 attendance-header">
        <h1 className="text-2xl font-bold">Employee Attendance</h1>
        <div className="flex items-center gap-4">
          <DatePicker
            onChange={handleMonthChange}
            picker="month"
            defaultValue={dayjs(
              `${selectedYear}-${String(selectedMonth).padStart(2, "0")}`
            )}
          />
          <div className="filter-panel-wrap" style={{ position: "relative" }}>
            <Badge
              count={
                (dateRange && (dateRange[0] || dateRange[1]) ? 1 : 0) +
                (selectedCompanyOfficeIds.length ? 1 : 0) +
                (selectedEmployeeIds.length ? 1 : 0)
              }
              size="small"
              offset={[-2, 2]}
            >
              <button
                type="button"
                className="filter-panel-trigger-blue"
                onClick={() => setFilterPanelOpen((o) => !o)}
                aria-label="Filters"
              >
                <FilterOutlined style={{ fontSize: 16 }} />
              </button>
            </Badge>
            <FilterPanel
              open={filterPanelOpen}
              onClose={() => setFilterPanelOpen(false)}
              dateRange={dateRange}
              onDateRangeChange={handleDateRangeChange}
              onClearAll={clearFilters}
              filters={[
                {
                  key: "office",
                  label: "Company",
                  placeholder: "Search or select company",
                  mode: "multiple",
                  value: selectedCompanyOfficeIds,
                  onChange: (values) => {
                    const nextValues = Array.isArray(values)
                      ? values.filter((value) => value !== "")
                      : [];
                    setSelectedCompanyOfficeIds(nextValues);
                    if (nextValues.length === 0) setSelectedEmployeeIds([]);
                  },
                  showSearch: true,
                  options: [
                    ...(masterData.CompanyOfficeMaster || []).map((o) => ({
                      value: o.CompanyOfficeId,
                      label: o.CompanyOfficeName,
                    })),
                  ],
                },
                {
                  key: "user",
                  label: "User",
                  placeholder: "Search by employee name or code",
                  mode: "multiple",
                  value: selectedEmployeeIds,
                  onChange: (values) =>
                    setSelectedEmployeeIds(
                      Array.isArray(values) ? values.filter((value) => value !== "") : []
                    ),
                  showSearch: true,
                  options: [
                    ...(availableEmployees || []).map((emp) => ({
                      value: emp.EmployeeId,
                      label: [formatEmployeeName(emp), emp.EmployeeCode].filter(Boolean).join(" • ") || "—",
                    })),
                  ],
                },
              ]}
            />
          </div>
          {canCreateAttendance && (
            <Button type="primary" onClick={showModal}>
              + Attendance
            </Button>
          )}
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleDownloadExcel}
          >
            Excel
          </Button>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleDownloadTimeSheetExcel}
          >
            Time Sheet
          </Button>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleDownloadPdf}
            loading={pdfLoading}
          >
            PDF
          </Button>
          {filteredEmployees && filteredEmployees.length > 0 && (
            <ColVisibilityDropdown
              options={columns}
              columns={columns}
              columnsToShowHandler={columnsToShowHandler}
            />
          )}
        </div>
      </div>

      <Modal
        title="Add Attendance"
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={500}
        footer={null}
      >
        <Attendance drawer={false} />
      </Modal>

      <div className="mb-4 flex gap-4 flex-wrap attendance-legend">
        {statusLegend.map(({ status, color }) => (
          <div key={status} className="flex items-center gap-2">
            <div className={`w-4 h-4 ${color} rounded-full`}></div>
            <span>{status}</span>
          </div>
        ))}
      </div>

      <div className="p-0 overflow-x-hidden attendance-table-container">
        {loading ? (
          <div className="attendance-loading-container">
            <Spin size="large" tip="Loading attendance data..." />
          </div>
        ) : (
          <div className="overflow-x-auto max-w-full" style={{ height: '100%' }}>
            <div className={`attendance-table-wrapper ${!filterPanelOpen ? 'filter-closed' : ''}`}>
              <table className="min-w-max border-collapse border border-gray-300" style={{ tableLayout: 'fixed' }}>
                <thead>
                  <tr>
                    {isColumnVisible("S.No.") && (
                      <th className="border border-gray-300 p-2 text-center bg-white z-30" rowSpan="2" style={{ position: 'sticky', top: 0, left: 0, zIndex: 30, backgroundColor: 'white', width: '50px', minWidth: '50px' }} data-column-id="S.No.">
                        S.No.
                      </th>
                    )}
                    {isColumnVisible("Office Location") && (
                      <th className="border border-gray-300 p-2 text-center bg-white z-30" rowSpan="2" style={{ position: 'sticky', top: 0, left: getStickyLeft("Office Location") + 'px', zIndex: 30, backgroundColor: 'white', width: '120px', minWidth: '120px' }} data-column-id="Office Location">
                        Office Location
                      </th>
                    )}
                    {isColumnVisible("Employee Code") && (
                      <th className="border border-gray-300 p-2 text-center bg-white z-30" rowSpan="2" style={{ position: 'sticky', top: 0, left: getStickyLeft("Employee Code") + 'px', zIndex: 30, backgroundColor: 'white', width: '100px', minWidth: '100px' }} data-column-id="Employee Code">
                        Employee Code
                      </th>
                    )}
                    {isColumnVisible("Employee Name") && (
                      <th className="border border-gray-300 p-2 text-center bg-white z-30" rowSpan="2" style={{ position: 'sticky', top: 0, left: getStickyLeft("Employee Name") + 'px', zIndex: 30, backgroundColor: 'white', width: '140px', minWidth: '140px' }} data-column-id="Employee Name">
                        Employee Name
                      </th>
                    )}
                    {isColumnVisible("Join Date") && (
                      <th className="border border-gray-300 p-2 text-center bg-white z-30" rowSpan="2" style={{ position: 'sticky', top: 0, left: getStickyLeft("Join Date") + 'px', zIndex: 30, backgroundColor: 'white', width: '110px', minWidth: '110px' }} data-column-id="Join Date">
                        Join Date
                      </th>
                    )}
                    {isColumnVisible("Designation") && (
                      <th className="border border-gray-300 p-2 text-center bg-white z-30" rowSpan="2" style={{ position: 'sticky', top: 0, left: getStickyLeft("Designation") + 'px', zIndex: 30, backgroundColor: 'white', width: '120px', minWidth: '120px' }} data-column-id="Designation">
                        Designation
                      </th>
                    )}
                    {isColumnVisible("Reporting To") && (
                      <th className="border border-gray-300 p-2 text-center bg-white z-30" rowSpan="2" style={{ position: 'sticky', top: 0, left: getStickyLeft("Reporting To") + 'px', zIndex: 30, backgroundColor: 'white', width: '140px', minWidth: '140px' }} data-column-id="Reporting To">
                        Reporting To
                      </th>
                    )}
                    {isColumnVisible("Daily Attendance") && daysArray.map((day, index) => (
                      <th
                        key={index}
                        className="border border-gray-300 p-2 text-center sticky top-0 bg-white z-10"
                        data-column-id="Daily Attendance"
                      >
                        {dateRange ? day.fullDate : day.day}
                        <br />({day.weekday})
                      </th>
                    ))}
                    {totalCols.some(col => isColumnVisible(totalColTitles[col] || col)) && (
                      <th className="border border-gray-300 p-2 text-center sticky top-0 bg-white z-10 bg-gray-100" colSpan={totalCols.filter(col => isColumnVisible(totalColTitles[col] || col)).length}>
                        Total
                      </th>
                    )}
                  </tr>
                  <tr>
                    {/* Empty cells to account for date columns in row 1 - needed for proper alignment */}
                    {isColumnVisible("Daily Attendance") && daysArray.map((day, index) => (
                      <th
                        key={`day-placeholder-${index}`}
                        className="border border-gray-300 p-0"
                        style={{ width: '5px', minWidth: '5px', padding: 0 }}
                      >
                      </th>
                    ))}
                    {totalCols.map((totalCol, idx) => {
                      const colTitle = totalColTitles[totalCol] || totalCol;
                      if (!isColumnVisible(colTitle)) return null;
                      const titleMap = {
                        "ECO": "Earn Combo Off Days",
                        "GPD": "Grace Presence Days",
                        "MPD": "Monthly Paid Days",
                        "TD": "Total Days",
                        "WD": "Working Days",
                        "APD": "Adjusted Partial Day",
                        "AAO": "Adjusted All Off",
                        "NFD": "Not Full Day"
                      };
                      return (
                        <th
                          key={`total-${idx}`}
                          className="border border-gray-300 p-2 text-center sticky top-0 bg-white z-10 bg-gray-50"
                          title={titleMap[totalCol] || ""}
                          data-column-id={colTitle}
                        >
                          {totalCol}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(groupedByCompany).length > 0 ? (
                    (() => {
                      let serialNumber = 1;
                      return Object.entries(groupedByCompany).flatMap(
                        ([comName, companyEmployees]) =>
                          companyEmployees.map((employee) => {
                            const {
                              EmployeeId,
                              attendance = [],
                            } = employee;
                            
                            const currentSerial = serialNumber++;
                            return (
                              <tr key={EmployeeId}>
                                {/* Serial Number */}
                                {isColumnVisible("S.No.") && (
                                  <td className="border border-gray-300 p-2 text-center bg-white" style={{ position: 'sticky', left: getStickyLeft("S.No.") + 'px', zIndex: 20, backgroundColor: 'white', width: '50px', minWidth: '50px' }} data-column-id="S.No.">
                                    {currentSerial}
                                  </td>
                                )}
                              
                              {/* Office Location */}
                              {isColumnVisible("Office Location") && (
                                <td className="border border-gray-300 p-2 text-left bg-white" style={{ position: 'sticky', left: getStickyLeft("Office Location") + 'px', zIndex: 20, backgroundColor: 'white', width: '120px', minWidth: '120px' }} data-column-id="Office Location">
                                  {getCompanyOfficeName(employee)}
                                </td>
                              )}
                              
                              {/* Employee Code */}
                              {isColumnVisible("Employee Code") && (
                                <td className="border border-gray-300 p-2 text-center bg-white" style={{ position: 'sticky', left: getStickyLeft("Employee Code") + 'px', zIndex: 20, backgroundColor: 'white', width: '100px', minWidth: '100px' }} data-column-id="Employee Code">
                                  {employee.EmployeeCode || ""}
                                </td>
                              )}
                              
                              {/* Employee Name */}
                              {isColumnVisible("Employee Name") && (
                                <td className="border border-gray-300 p-2 text-left bg-white" style={{ position: 'sticky', left: getStickyLeft("Employee Name") + 'px', zIndex: 20, backgroundColor: 'white', width: '140px', minWidth: '140px' }} data-column-id="Employee Name">
                                {formatEmployeeName(employee)}
                              </td>
                              )}

                              {/* Join Date */}
                              {isColumnVisible("Join Date") && (
                                <td className="border border-gray-300 p-2 text-center bg-white" style={{ position: 'sticky', left: getStickyLeft("Join Date") + 'px', zIndex: 20, backgroundColor: 'white', width: '110px', minWidth: '110px' }} data-column-id="Join Date">
                                  {formatJoinDate(employee.JoinDate)}
                                </td>
                              )}
                              
                              {/* Designation */}
                              {isColumnVisible("Designation") && (
                                <td className="border border-gray-300 p-2 text-left bg-white" style={{ position: 'sticky', left: getStickyLeft("Designation") + 'px', zIndex: 20, backgroundColor: 'white', width: '120px', minWidth: '120px' }} data-column-id="Designation">
                                  {employee.DesignationName || ""}
                              </td>
                              )}
                              
                              {/* Reporting To */}
                              {isColumnVisible("Reporting To") && (
                                <td className="border border-gray-300 p-2 text-left bg-white" style={{ position: 'sticky', left: getStickyLeft("Reporting To") + 'px', zIndex: 20, backgroundColor: 'white', width: '140px', minWidth: '140px' }} data-column-id="Reporting To">
                                {employee.ReportingToName || ""}
                              </td>
                              )}
                              
                              {/* Daily attendance */}
                              {isColumnVisible("Daily Attendance") && daysArray.map((day, index) => {
                                const record =
                                  attendance.find(
                                    (r) => r?.AttendanceDate === day.dateStr
                                  ) || {};
                                const inTime = record.PunchInTime
                                  ? record.PunchInTime
                                  : "";
                                const outTime = record.PunchOutTime
                                  ? record.PunchOutTime
                                  : "";
                                // Use the same logic as Excel export
                                const statusCode = getAttendanceStatus(record);
                                const statusMap = {
                                  "P": "Present",
                                  "A": "Absent",
                                  "L": "Leave",
                                  "H": "HalfDay",
                                  "SL": "ShortLeave",
                                  "WO": "Weekoff",
                                  "PH": "Holiday",
                                  "RH": "Restricted"
                                };
                                const status = statusCode ? (statusMap[statusCode] || "Absent") : "Absent";
                                
                                const statusColor =
                                  status === "Present"
                                    ? "bg-green-100 text-green-800"
                                    : status === "Absent"
                                    ? "bg-red-100 text-red-800"
                                    : status === "Weekoff"
                                    ? "bg-yellow-100 text-yellow-800"
                                    : status === "Holiday"
                                    ? "bg-purple-100 text-purple-800"
                                    : status === "Leave"
                                    ? "bg-orange-100 text-orange-800"
                                    : status === "HalfDay"
                                    ? "bg-cyan-100 text-cyan-800"
                                    : status === "ShortLeave"
                                    ? "bg-pink-100 text-pink-800"
                                    : "bg-gray-100 text-gray-800";

                                return (
                                  <td
                                    key={index}
                                    className={`border border-gray-300 p-2 text-center ${statusColor}`}
                                    data-column-id="Daily Attendance"
                                  >
                                    <div className="flex flex-col items-center">
                                      <div className="inline-flex items-center gap-1">
                                        <span>{inTime}</span>
                                        <span>-</span>
                                        <span>{outTime}</span>
                                      </div>
                                      {record.IsPresent && record.WorkingHours && (
                                        <div className="text-xs mt-1">
                                          ({record.WorkingHours})
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                );
                              })}
                              
                              {/* Summary columns - Get from API data */}
                              {isColumnVisible("Present") && (
                                <td className="border border-gray-300 p-2 text-center bg-green-50" data-column-id="Present">
                                  {employee.PresentDays || 0}
                                </td>
                              )}
                              {isColumnVisible("Absent") && (
                                <td className="border border-gray-300 p-2 text-center bg-red-50" data-column-id="Absent">
                                  {employee.AbsentDays || 0}
                                </td>
                              )}
                              {isColumnVisible("Leave") && (
                                <td className="border border-gray-300 p-2 text-center bg-orange-50" data-column-id="Leave">
                                  {employee.LeaveDays || 0}
                                </td>
                              )}
                              {isColumnVisible("Half Day") && (
                                <td className="border border-gray-300 p-2 text-center bg-cyan-50" data-column-id="Half Day">
                                  {employee.HalfDays || 0}
                                </td>
                              )}
                              {isColumnVisible("Short Days") && (
                                <td className="border border-gray-300 p-2 text-center bg-pink-50" data-column-id="Short Days">
                                  {employee.ShortDays || 0}
                                </td>
                              )}
                              {isColumnVisible("Weekoff") && (
                                <td className="border border-gray-300 p-2 text-center bg-yellow-50" data-column-id="Weekoff">
                                  {employee.WeekOffs || 0}
                                </td>
                              )}
                              {isColumnVisible("Public Holiday") && (
                                <td className="border border-gray-300 p-2 text-center bg-purple-50" data-column-id="Public Holiday">
                                  {employee.PublicHolidays || 0}
                                </td>
                              )}
                              {isColumnVisible("Restricted Holiday") && (
                                <td className="border border-gray-300 p-2 text-center bg-gray-50" data-column-id="Restricted Holiday">
                                  {employee.RestrictedHolidays || 0}
                                </td>
                              )}
                              {isColumnVisible("Not Full Day") && (
                                <td className="border border-gray-300 p-2 text-center bg-slate-50" data-column-id="Not Full Day">
                                  {employee.NotFullDay || 0}
                                </td>
                              )}
                              {isColumnVisible("Earn Combo Off Days") && (
                                <td className="border border-gray-300 p-2 text-center bg-blue-50" data-column-id="Earn Combo Off Days">
                                  {employee.EarnComboOffDays || 0}
                                </td>
                              )}
                              {isColumnVisible("Grace Presence Days") && (
                                <td className="border border-gray-300 p-2 text-center bg-indigo-50" data-column-id="Grace Presence Days">
                                  {employee.GracePresenceDays || 0}
                                </td>
                              )}
                              {isColumnVisible("Adjusted Partial Day") && (
                                <td className="border border-gray-300 p-2 text-center bg-violet-50" data-column-id="Adjusted Partial Day">
                                  {employee.AdjestedPartialDay || 0}
                                </td>
                              )}
                              {isColumnVisible("Adjusted All Off") && (
                                <td className="border border-gray-300 p-2 text-center bg-fuchsia-50" data-column-id="Adjusted All Off">
                                  {employee.AdjestedAllOff || 0}
                                </td>
                              )}
                              {isColumnVisible("Monthly Paid Days") && (
                                <td className="border border-gray-300 p-2 text-center bg-blue-50 font-semibold" data-column-id="Monthly Paid Days">
                                  {employee.MonthelyPaidDays || 0}
                                </td>
                              )}
                              {isColumnVisible("Total Days") && (
                                <td className="border border-gray-300 p-2 text-center bg-teal-50" data-column-id="Total Days">
                                  {employee.TotalDays || 0}
                                </td>
                              )}
                              {isColumnVisible("Working Days") && (
                                <td className="border border-gray-300 p-2 text-center bg-green-50" data-column-id="Working Days">
                                  {employee.WorkingDays || 0}
                                </td>
                              )}
                            </tr>
                          );
                        })
                      );
                    })()
                  ) : (
                    <tr>
                      <td
                        colSpan={
                          (isColumnVisible("S.No.") ? 1 : 0) +
                          (isColumnVisible("Office Location") ? 1 : 0) +
                          (isColumnVisible("Employee Code") ? 1 : 0) +
                          (isColumnVisible("Employee Name") ? 1 : 0) +
                          (isColumnVisible("Join Date") ? 1 : 0) +
                          (isColumnVisible("Designation") ? 1 : 0) +
                          (isColumnVisible("Reporting To") ? 1 : 0) +
                          (isColumnVisible("Daily Attendance") ? daysArray.length : 0) +
                          totalCols.filter(col => isColumnVisible(totalColTitles[col] || col)).length
                        }
                        className="border border-gray-300 p-2 text-center"
                      >
                        No attendance data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllAttendanceCalendar;
